import {
  DEFAULT_PHONE_COUNTRY_CODE,
  formatPhoneNumberToDisplay,
  sanitizePhoneNumber,
} from '@helpers/utils';
import {
  ErrorDetailsDtoErrorCodeEnum,
  ResponseErrorDto,
  EditUserDto,
  Role,
} from '@interfaces/models';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useCurrentUser, UserMetadata } from '@shared/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '@shared/stores';
import { useForm } from 'react-hook-form';
import { updateUser } from '@api/users';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { z } from 'zod';

type UserFormValues = z.infer<ReturnType<typeof formSchema>>;

const USER_FORM_DEFAULT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  phoneExt: DEFAULT_PHONE_COUNTRY_CODE,
  phoneNumber: '',
  role: '',
  marketingPhoneManagementAccepted: false,
  marketingEmailManagementAccepted: false,
};

const formDefaultValue = (user: UserMetadata, role: string | null) => {
  if (!user) return USER_FORM_DEFAULT_VALUES;

  return {
    firstName: user.firstName ?? USER_FORM_DEFAULT_VALUES.firstName,
    lastName: user.lastName ?? USER_FORM_DEFAULT_VALUES.lastName,
    email: user.email ?? USER_FORM_DEFAULT_VALUES.email,
    phoneExt: user.phoneExt ?? USER_FORM_DEFAULT_VALUES.phoneExt,
    phoneNumber:
      formatPhoneNumberToDisplay(user.phoneNumber) ?? USER_FORM_DEFAULT_VALUES.phoneNumber,
    marketingPhoneManagementAccepted: user.marketingPhoneManagementAccepted ?? false,
    marketingEmailManagementAccepted: user.marketingEmailManagementAccepted ?? false,
    role: role ?? USER_FORM_DEFAULT_VALUES.role,
  };
};

const formSchema = (t: TFunction) =>
  z.object({
    firstName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
    lastName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
    email: z.string({ required_error: t('FORMS.REQUIRED') }).email(),
    phoneExt: z.string({ required_error: t('FORMS.REQUIRED') }),
    phoneNumber: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .min(9, t('FORMS.MIN_CHARACTERS', { limit: 7 })) // min 9 because validator count also white spaces from mask
      .max(19, t('FORMS.MAX_CHARACTERS', { limit: 15 })) // 15 is max, we have 4 spaces in mask
      .or(z.literal('')),
    role: z.string({ required_error: t('FORMS.REQUIRED') }),
    marketingPhoneManagementAccepted: z.boolean(),
    marketingEmailManagementAccepted: z.boolean(),
  });

export const useCurrentUserEdit = (onSuccess: () => void) => {
  const { updateUser: updateUserStore } = useUserStore();
  const { user: data, role } = useCurrentUser();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const {
    mutate,
    isPending: isUpdatePending,
    isSuccess: isUpdateSuccess,
  } = useMutation({
    mutationFn: updateUser,
    onSuccess: (response) => {
      updateUserStore(response);
      toast.success(t('CURRENT_USER_EDIT.SUCCESS'));
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      if (error.errorCode === ErrorDetailsDtoErrorCodeEnum.USER_EDIT_PHONE_NUMBER_NOT_UNIQUE) {
        return form.setError('phoneNumber', {
          message: t(`API_ERRORS.${error.errorCode}`),
          type: 'custom',
        });
      }

      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const form = useForm({
    resolver: zodResolver(formSchema(t)),
    defaultValues: formDefaultValue(data, role),
    mode: 'onBlur',
  });

  const phoneIsInvalid = form.getFieldState('phoneNumber').invalid;

  const isSubmitDisabled = useMemo(
    () => isUpdatePending || phoneIsInvalid,
    [isUpdatePending, phoneIsInvalid],
  );

  const onSubmit = useCallback(
    (value: UserFormValues) => {
      if (!data) return;
      const { role, ...val } = value;

      const updatedUser: EditUserDto = {
        ...val,
        role: role as Role,
      };

      mutate({
        id: data.id,
        data: { ...updatedUser, phoneNumber: sanitizePhoneNumber(updatedUser.phoneNumber || '') },
      });
    },
    [mutate, data],
  );

  useEffect(() => {
    // Reset form values when user changes. Or when user is not loaded yet.
    const user = formDefaultValue(data, role);
    form.reset(user);
  }, [data, role, form]);

  return {
    isSubmitDisabled,
    isUpdateSuccess,
    isUpdatePending,
    form,
    t,
    onSubmit,
  };
};
